// gym/GymCoachAssignments.jsx - Version corrigée
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Alert, Snackbar, Chip,
  FormControl, InputLabel, Select, MenuItem,
  Tooltip, Card, CardContent, Avatar,
  CircularProgress, InputAdornment
} from '@mui/material';
import {
  Add, Edit, Delete, People, CheckCircle,
  Cancel, Search, Refresh, Person,
  Phone, Email, CalendarToday, AccessTime,
  SportsGymnastics, Euro
} from '@mui/icons-material';
import api from '../services/auth';

const GymCoachAssignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [clients, setClients] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [coachFilter, setCoachFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [formData, setFormData] = useState({
    client: null,
    coach: null,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    sessions_per_week: 3,
    session_duration_minutes: 60,
    total_price_htg: '0.00',
    status: 'ACTIVE',
    notes: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadingClientsCoaches, setLoadingClientsCoaches] = useState(false);

  const STATUS_CHOICES = [
    { value: 'ACTIVE', label: 'Actif', color: 'success' },
    { value: 'COMPLETED', label: 'Terminé', color: 'info' },
    { value: 'CANCELLED', label: 'Annulé', color: 'error' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterAssignments();
  }, [assignments, searchTerm, statusFilter, coachFilter, clientFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching all data...');
      await Promise.all([
        fetchAssignments(),
        fetchClients(),
        fetchCoaches()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des données',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async () => {
    try {
      console.log('📋 Fetching assignments...');
      const response = await api.get('/gym/coach-assignments/');
      console.log('📋 Assignments raw response:', response.data);
      
      // Gestion de la pagination
      let assignmentsData = [];
      if (response.data && typeof response.data === 'object') {
        if (response.data.results) {
          // Pagination Django REST framework
          assignmentsData = response.data.results;
          console.log('📄 Assignments pagination detected, total:', response.data.count);
        } else if (Array.isArray(response.data)) {
          assignmentsData = response.data;
        }
      }
      
      console.log('📋 Processed assignments:', assignmentsData);
      setAssignments(assignmentsData);
    } catch (error) {
      console.error('❌ Error fetching assignments:', error);
      setAssignments([]);
    }
  };

  const fetchClients = async () => {
    try {
      setLoadingClientsCoaches(true);
      console.log('👥 Fetching clients...');
      const response = await api.get('/gym/clients/');
      console.log('👥 Clients response:', response.data);
      
      // Gestion de la pagination
      let clientsData = [];
      if (response.data && typeof response.data === 'object') {
        if (response.data.results) {
          // Pagination Django REST framework
          clientsData = response.data.results;
          console.log('📄 Clients pagination detected, total:', response.data.count);
        } else if (Array.isArray(response.data)) {
          clientsData = response.data;
        }
      }
      
      console.log('👥 Processed clients:', clientsData);
      console.log('👥 Number of clients loaded:', clientsData.length);
      setClients(clientsData);
    } catch (error) {
      console.error('❌ Error fetching clients:', error);
      console.error('🔍 Error details:', error.response?.data);
      setClients([]);
    } finally {
      setLoadingClientsCoaches(false);
    }
  };

  const fetchCoaches = async () => {
    try {
      console.log('🏋️‍♂️ Fetching coaches...');
      const response = await api.get('/gym/coaches/');
      console.log('🏋️‍♂️ Coaches response:', response.data);
      
      // Gestion de la pagination
      let coachesData = [];
      if (response.data && typeof response.data === 'object') {
        if (response.data.results) {
          // Pagination Django REST framework
          coachesData = response.data.results;
          console.log('📄 Coaches pagination detected, total:', response.data.count);
        } else if (Array.isArray(response.data)) {
          coachesData = response.data;
        }
      }
      
      console.log('🏋️‍♂️ Processed coaches:', coachesData);
      console.log('🏋️‍♂️ Number of coaches loaded:', coachesData.length);
      setCoaches(coachesData);
    } catch (error) {
      console.error('❌ Error fetching coaches:', error);
      console.error('🔍 Error details:', error.response?.data);
      setCoaches([]);
    }
  };

  // Fonction pour trouver un client par ID
  const findClientById = (clientId) => {
    if (!clientId) return null;
    const client = getClientsArray().find(c => c.id === clientId);
    console.log(`🔍 Finding client ${clientId}:`, client);
    return client;
  };

  // Fonction pour trouver un coach par ID
  const findCoachById = (coachId) => {
    if (!coachId) return null;
    const coach = getCoachesArray().find(c => c.id === coachId);
    console.log(`🔍 Finding coach ${coachId}:`, coach);
    return coach;
  };

  // Fonction utilitaire pour obtenir un tableau d'assignations
  const getAssignmentsArray = () => {
    return Array.isArray(assignments) ? assignments : [];
  };

  // Fonction utilitaire pour obtenir un tableau de clients
  const getClientsArray = () => {
    return Array.isArray(clients) ? clients : [];
  };

  // Fonction utilitaire pour obtenir un tableau de coachs
  const getCoachesArray = () => {
    return Array.isArray(coaches) ? coaches : [];
  };

  // Fonction pour enrichir une assignation avec les données client/coach
  const enrichAssignment = (assignment) => {
    // L'API peut retourner soit un ID, soit un objet complet
    const client = assignment.client?.id 
      ? findClientById(assignment.client.id) 
      : findClientById(assignment.client);
    
    const coach = assignment.coach?.id 
      ? findCoachById(assignment.coach.id) 
      : findCoachById(assignment.coach);
    
    return {
      ...assignment,
      _client: client, // Stocker l'objet client complet
      _coach: coach    // Stocker l'objet coach complet
    };
  };

  const filterAssignments = () => {
    console.log('🔍 Filtering assignments...');
    
    const assignmentsArray = getAssignmentsArray();
    console.log('📋 Assignments array sample:', assignmentsArray.length > 0 ? assignmentsArray[0] : 'Empty');
    
    // Enrichir les assignations avec les données client/coach
    const enrichedAssignments = assignmentsArray.map(enrichAssignment);
    
    console.log('🎯 Enriched assignments sample:', enrichedAssignments.length > 0 ? enrichedAssignments[0] : 'Empty');
    
    let filtered = enrichedAssignments;

    if (searchTerm) {
      filtered = filtered.filter(assignment => {
        const client = assignment._client;
        const coach = assignment._coach;
        const clientName = client ? `${client.first_name || ''} ${client.last_name || ''}`.toLowerCase() : '';
        const coachName = coach ? `${coach.first_name || ''} ${coach.last_name || ''}`.toLowerCase() : '';
        return (
          clientName.includes(searchTerm.toLowerCase()) ||
          coachName.includes(searchTerm.toLowerCase()) ||
          assignment.notes?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(assignment => assignment.status === statusFilter);
    }

    if (coachFilter !== 'all') {
      filtered = filtered.filter(assignment => {
        const coach = assignment._coach;
        return coach && coach.id === parseInt(coachFilter);
      });
    }

    if (clientFilter !== 'all') {
      filtered = filtered.filter(assignment => {
        const client = assignment._client;
        return client && client.id === parseInt(clientFilter);
      });
    }

    console.log('✅ Filtered assignments:', filtered);
    console.log('🔢 Filtered count:', filtered.length);
    
    setFilteredAssignments(filtered);
  };

  const handleOpenDialog = (assignment = null) => {
    console.log('📝 Opening dialog...');
    console.log('👥 Available clients:', getClientsArray());
    console.log('🏋️‍♂️ Available coaches:', getCoachesArray());
    
    if (assignment) {
      console.log('✏️ Editing assignment:', assignment);
      
      // Pour l'édition, utiliser les données enrichies
      const enrichedAssignment = enrichAssignment(assignment);
      
      setEditingAssignment(assignment);
      setFormData({
        client: enrichedAssignment._client || null,
        coach: enrichedAssignment._coach || null,
        start_date: assignment.start_date || new Date().toISOString().split('T')[0],
        end_date: assignment.end_date || '',
        sessions_per_week: assignment.sessions_per_week || 3,
        session_duration_minutes: assignment.session_duration_minutes || 60,
        total_price_htg: assignment.total_price_htg || '0.00',
        status: assignment.status || 'ACTIVE',
        notes: assignment.notes || ''
      });
    } else {
      console.log('➕ Creating new assignment');
      setEditingAssignment(null);
      setFormData({
        client: null,
        coach: null,
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        sessions_per_week: 3,
        session_duration_minutes: 60,
        total_price_htg: '0.00',
        status: 'ACTIVE',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    console.log('❌ Closing dialog');
    setOpenDialog(false);
    setEditingAssignment(null);
    setSubmitting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // VÉRIFIER LES DONNÉES
      console.log('📋 Form data before sending:', formData);
      console.log('👥 Selected client ID:', formData.client?.id);
      console.log('🏋️‍♂️ Selected coach ID:', formData.coach?.id);

      if (!formData.client?.id || !formData.coach?.id) {
        throw new Error('Client ou Coach manquant. Veuillez sélectionner un client et un coach.');
      }

      // Préparer les données pour l'API
      const apiData = {
        client: formData.client.id,  // Envoyer seulement l'ID
        coach: formData.coach.id,    // Envoyer seulement l'ID
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        sessions_per_week: parseInt(formData.sessions_per_week) || 3,
        session_duration_minutes: parseInt(formData.session_duration_minutes) || 60,
        total_price_htg: parseFloat(formData.total_price_htg) || 0,
        status: formData.status,
        notes: formData.notes || ''
      };

      console.log('🚀 Submitting assignment data:', apiData);

      let response;
      if (editingAssignment) {
        console.log(`📝 Updating assignment ${editingAssignment.id}...`);
        response = await api.put(`/gym/coach-assignments/${editingAssignment.id}/`, apiData);
        console.log('✅ Update response:', response.data);
        setSnackbar({ open: true, message: 'Assignation modifiée avec succès', severity: 'success' });
      } else {
        console.log('🆕 Creating new assignment...');
        response = await api.post('/gym/coach-assignments/', apiData);
        console.log('✅ Create response:', response.data);
        console.log('✅ New assignment ID:', response.data.id);
        setSnackbar({ open: true, message: 'Assignation créée avec succès', severity: 'success' });
      }

      handleCloseDialog();
      
      // Rafraîchir la liste après un court délai
      setTimeout(() => {
        console.log('🔄 Refreshing assignments list...');
        fetchAssignments();
      }, 500);

    } catch (error) {
      console.error('❌ Error submitting assignment:', error);
      console.error('🔍 Error details:', error.response?.data);

      let errorMessage = 'Erreur lors de l\'opération';
      if (error.response?.data) {
        if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else {
          const errors = Object.values(error.response.data).flat();
          errorMessage = errors.join(', ');
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette assignation?')) {
      try {
        console.log('🗑️ Deleting assignment:', id);
        await api.delete(`/gym/coach-assignments/${id}/`);
        setSnackbar({ open: true, message: 'Assignation supprimée avec succès', severity: 'success' });
        fetchAssignments();
      } catch (error) {
        console.error('Error deleting assignment:', error);
        setSnackbar({ open: true, message: 'Erreur lors de la suppression', severity: 'error' });
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR');
    } catch (e) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'HTG',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getStatusLabel = (value) => {
    const status = STATUS_CHOICES.find(s => s.value === value);
    return status ? status.label : value;
  };

  const getStatusColor = (value) => {
    const status = STATUS_CHOICES.find(s => s.value === value);
    return status ? status.color : 'default';
  };

  const calculateWeeklyHours = (sessionsPerWeek, durationMinutes) => {
    return (sessionsPerWeek * durationMinutes) / 60;
  };

  // Calcul des statistiques
  const assignmentsArray = getAssignmentsArray();
  const stats = {
    total: assignmentsArray.length,
    active: assignmentsArray.filter(a => a.status === 'ACTIVE').length,
    completed: assignmentsArray.filter(a => a.status === 'COMPLETED').length,
    cancelled: assignmentsArray.filter(a => a.status === 'CANCELLED').length
  };

  const totalWeeklyHours = assignmentsArray
    .filter(a => a.status === 'ACTIVE')
    .reduce((total, a) => total + calculateWeeklyHours(a.sessions_per_week, a.session_duration_minutes), 0);

  return (
    <Box p={3}>
      {/* Bouton de débogage */}
      <Box sx={{ mb: 2 }}>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <People color="primary" />
                <Typography variant="h6">Total Assignations</Typography>
              </Box>
              <Typography variant="h4" color="primary.main">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: 'success.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircle />
                <Typography variant="h6">Actives</Typography>
              </Box>
              <Typography variant="h4">
                {stats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: 'info.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <SportsGymnastics />
                <Typography variant="h6">Heures/semaine</Typography>
              </Box>
              <Typography variant="h4">
                {totalWeeklyHours.toFixed(1)}h
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarToday />
                <Typography variant="h6">Terminées</Typography>
              </Box>
              <Typography variant="h4">
                {stats.completed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">🤝 Assignations Coach-Client</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={submitting || loadingClientsCoaches}
        >
          {submitting ? <CircularProgress size={24} /> : 'Nouvelle Assignation'}
        </Button>
      </Box>

      {/* Barre de recherche et filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Rechercher client, coach, notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                {STATUS_CHOICES.map(status => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Coach</InputLabel>
              <Select
                value={coachFilter}
                label="Coach"
                onChange={(e) => setCoachFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                {getCoachesArray().map(coach => (
                  <MenuItem key={coach.id} value={coach.id}>
                    {coach.first_name} {coach.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Client</InputLabel>
              <Select
                value={clientFilter}
                label="Client"
                onChange={(e) => setClientFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                {getClientsArray().map(client => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.first_name} {client.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchData}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Actualiser'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tableau */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.light' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Client</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Coach</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Période</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Programme</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Prix Total</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Chargement des assignations...</Typography>
                </TableCell>
              </TableRow>
            ) : !Array.isArray(filteredAssignments) || filteredAssignments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  {assignmentsArray.length === 0 ? (
                    <Box>
                      <Typography variant="h6" color="textSecondary" gutterBottom>
                        Aucune assignation enregistrée
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Cliquez sur "Nouvelle Assignation" pour créer votre première assignation.
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="h6" color="textSecondary" gutterBottom>
                        Aucune assignation trouvée avec les filtres actuels
                      </Typography>
                      <Button
                        variant="outlined"
                        sx={{ mt: 2 }}
                        onClick={() => {
                          setSearchTerm('');
                          setStatusFilter('all');
                          setCoachFilter('all');
                          setClientFilter('all');
                        }}
                      >
                        Réinitialiser les filtres
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filteredAssignments.map((assignment) => {
                const client = assignment._client;
                const coach = assignment._coach;
                
                console.log('🎯 Rendering assignment:', { 
                  id: assignment.id, 
                  client, 
                  coach,
                  clientId: assignment.client,
                  coachId: assignment.coach
                });

                return (
                  <TableRow key={assignment.id} hover>
                    <TableCell>
                      {client ? (
                        <Box display="flex" alignItems="center" gap={2}>
                            {/* {client.first_name}{client.last_name} */}
                          <Box>
                            <Typography fontWeight="bold">
                              {client.first_name} {client.last_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {client.code}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Box>
                          <Typography color="textSecondary">
                            Client ID
                          </Typography>
                          <Typography variant="caption" color="error">
                            (Non trouvé dans la liste)
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {coach ? (
                        <Box display="flex" alignItems="center" gap={2}>
                            {/* {coach.first_name}{coach.last_name} */}
                          <Box>
                            <Typography fontWeight="bold">
                              {coach.first_name} {coach.last_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {coach.speciality}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Box>
                          <Typography color="textSecondary">
                            Coach ID
                          </Typography>
                          <Typography variant="caption" color="error">
                            (Non trouvé dans la liste)
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CalendarToday fontSize="small" color="action" />
                          <Typography variant="body2">
                            Du {formatDate(assignment.start_date)}
                          </Typography>
                        </Box>
                        {assignment.end_date && (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <CalendarToday fontSize="small" color="action" />
                            <Typography variant="body2">
                              Au {formatDate(assignment.end_date)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <AccessTime fontSize="small" color="action" />
                          <Typography variant="body2">
                            {assignment.sessions_per_week} séances/semaine
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                          {assignment.session_duration_minutes} min par séance
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {calculateWeeklyHours(assignment.sessions_per_week, assignment.session_duration_minutes).toFixed(1)}h/semaine
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography fontWeight="bold">
                          {formatCurrency(assignment.total_price_htg)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(assignment.status)}
                        color={getStatusColor(assignment.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Modifier">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleOpenDialog(assignment)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDelete(assignment.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pour créer/modifier */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAssignment ? '✏️ Modifier Assignation' : '➕ Nouvelle Assignation'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Client *</InputLabel>
                  <Select
                    value={formData.client?.id || ''}
                    label="Client *"
                    onChange={(e) => {
                      const client = getClientsArray().find(c => c.id === parseInt(e.target.value));
                      console.log('👥 Selected client:', client);
                      setFormData({ ...formData, client: client || null });
                    }}
                    disabled={loadingClientsCoaches}
                  >
                    {getClientsArray().map(client => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.first_name} {client.last_name} ({client.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Coach *</InputLabel>
                  <Select
                    value={formData.coach?.id || ''}
                    label="Coach *"
                    onChange={(e) => {
                      const coach = getCoachesArray().find(c => c.id === parseInt(e.target.value));
                      console.log('🏋️‍♂️ Selected coach:', coach);
                      setFormData({ ...formData, coach: coach || null });
                    }}
                    disabled={loadingClientsCoaches}
                  >
                    {getCoachesArray().map(coach => (
                      <MenuItem key={coach.id} value={coach.id}>
                        {coach.first_name} {coach.last_name} ({coach.speciality})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date de début *"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date de fin (optionnel)"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Séances par semaine *"
                  type="number"
                  value={formData.sessions_per_week}
                  onChange={(e) => setFormData({ ...formData, sessions_per_week: e.target.value })}
                  required
                  margin="normal"
                  inputProps={{ min: 1, max: 7 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Durée par séance (minutes) *"
                  type="number"
                  value={formData.session_duration_minutes}
                  onChange={(e) => setFormData({ ...formData, session_duration_minutes: e.target.value })}
                  required
                  margin="normal"
                  inputProps={{ min: 15, max: 180, step: 15 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prix total (HTG) *"
                  type="number"
                  value={formData.total_price_htg}
                  onChange={(e) => setFormData({ ...formData, total_price_htg: e.target.value })}
                  required
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">HTG</InputAdornment>,
                  }}
                  inputProps={{ min: "0.01", step: "0.01" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.status}
                    label="Statut"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    {STATUS_CHOICES.map(status => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes (optionnel)"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  multiline
                  rows={3}
                  margin="normal"
                  placeholder="Informations supplémentaires..."
                />
              </Grid>
              {/* {formData.client && formData.coach && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Récapitulatif :
                    </Typography>
                    <Typography variant="body2">
                      {formData.client.first_name} {formData.client.last_name} avec {formData.coach.first_name} {formData.coach.last_name}
                    </Typography>
                    <Typography variant="body2">
                      Programme : {formData.sessions_per_week} séances de {formData.session_duration_minutes} min par semaine
                      ({calculateWeeklyHours(formData.sessions_per_week, formData.session_duration_minutes).toFixed(1)}h/semaine)
                    </Typography>
                  </Paper>
                </Grid>
              )} */}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={submitting}>
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting || !formData.client || !formData.coach || !formData.start_date}
            >
              {submitting ? <CircularProgress size={24} /> : editingAssignment ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GymCoachAssignments;